// src/pages/evcc/EvccModelDocuments.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#ccd87cff";
const sectionHeaderBorder = "#9fad3d";
const linkBlue = "#7e895d";

const getFileIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "pdf") {
    return <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />;
  }
  // doc / docx / default
  return <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />;
};

/* ---------- Data ---------- */

const introSections = [
  {
    title: "Cover Sheet for Model Documents",
    intro:
      "A reference and introductory document that explains how to use each of the EVCC model documents. It also provides design and branding guidance, including instructions for incorporating the EVCC logo.",
    items: [
      {
        title: "Cover Sheet for EVCC Model Documents (pdf)",
        description:
          "Overview of the model documents, how they fit together, and how to apply EVCC branding correctly.",
        href: "/assets/evcc/model-docs/cover-sheet-model-documents.pdf", // update path
        type: "pdf",
      },
    ],
  },
  {
    title: "ccr_guidance_v1.pdf",
    intro:
      "Guidance document to help you identify which contract you should be using and when.",
    items: [
      {
        title: "ccr_guidance_v1.pdf",
        description:
          "Complying with the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 – scenario-based guidance for EVCC members.",
        href: "/assets/evcc/model-docs/ccr_guidance_v1.pdf", // update path
        type: "pdf",
      },
    ],
  },
];

const modelContractsSection = {
  title: "Model Contract Documents",
  items: [
    {
      title: "Contract (off-premises)",
      description:
        "Use this contract when a sale is agreed away from your trade premises, such as in a consumer’s home or entirely at a distance (for example, online).",
      href: "/assets/evcc/model-docs/contract-off-premises.docx", // update path/ext
      type: "docx",
    },
    {
      title: "Contract (on-premises)",
      description:
        "Use this contract when a sale is agreed on your trade premises.",
      href: "/assets/evcc/model-docs/contract-on-premises.docx", // update path/ext
      type: "docx",
    },
    {
      title: "Quotation",
      description:
        "Provides a clear, written quotation setting out the proposed work, price, and key assumptions for the consumer.",
      href: "/assets/evcc/model-docs/quotation.docx", // update path/ext
      type: "docx",
    },
    {
      title: "Express request form",
      description:
        "Confirms a customer’s agreement to begin work before the usual cancellation period ends. Their permission must be given in writing and retained on record.",
      href: "/assets/evcc/model-docs/express-request-form.docx", // update path/ext
      type: "docx",
    },
    {
      title: "Cancellation form",
      description:
        "This form must be provided to consumers alongside their Quotation and/or Contract. While consumers are not required to use this form to cancel, it must be available to them as an option. The wording of this form must not be altered.",
      href: "/assets/evcc/model-docs/cancellation-form.docx", // update path/ext
      type: "docx",
    },
  ],
};

/* ---------- UI components ---------- */

function SectionCard({ title, intro, items }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        {intro && (
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#4b5563", maxWidth: 900 }}
          >
            {intro}
          </Typography>
        )}

        <List disablePadding>
          {items.map((item, index) => (
            <React.Fragment key={item.title}>
              <ListItemButton
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  alignItems: "flex-start",
                  px: 0,
                  "&:hover .linkText": { textDecoration: "underline" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: sectionHeaderBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getFileIcon(item.type)}
                  </Box>
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      className="linkText"
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: linkBlue,
                      }}
                    >
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    item.description && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: "#4a4f59", lineHeight: 1.5 }}
                      >
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItemButton>

              {index < items.length - 1 && (
                <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Paper>
  );
}

export default function EvccModelDocuments() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; EVCC &gt; Model Documents
        </Typography>

        {/* Page title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          EVCC – Model Documents
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          Use these EVCC model contracts and forms to ensure your consumer
          documentation is clear, compliant and aligned with the latest
          consumer protection requirements.
        </Typography>

        {/* Intro docs */}
        {introSections.map((section) => (
          <SectionCard
            key={section.title}
            title={section.title}
            intro={section.intro}
            items={section.items}
          />
        ))}

        {/* Model contracts */}
        <SectionCard
          title={modelContractsSection.title}
          intro={null}
          items={modelContractsSection.items}
        />
      </Container>
    </Box>
  );
}
